import bg3 from "../images/bg3.webp";
import { getVendorLogoCollectionByGroup } from "#root/schema/vendor";
import { useData } from "#root/utility/hooks";
import type { ReactNode } from "react";
import { TbBolt, TbCurrencyDollar, TbDatabase, TbTrendingUp, TbWorld } from "react-icons/tb";
import { Link } from "react-router-dom";
import PageLayout from "./layout";

export default function HomePage(): ReactNode {
  const [dataSources] = useData(async () => {
    const { rows } = await getVendorLogoCollectionByGroup("dataSource").list();
    return rows;
  });

  const [newsSources] = useData(async () => {
    const { rows } = await getVendorLogoCollectionByGroup("newsSource").list();
    return rows;
  });

  return (
    <PageLayout title="CloudQuote API Platform">
      <div className="w-full min-h-0 p-0">
        {/* Jumbotron */}
        <section
          className="flex-none m-0 flex justify-center bg-cover bg-center"
          style={{
            background:
              `linear-gradient(90deg, rgb(0 0 0 / 0.5), rgb(0 0 0 / 0.3)), url("${bg3}") center / cover`,
          }}
        >
          <div className="text-white w-full max-w-240 my-20 px-15 py-10 rounded-2xl backdrop-blur-[2px] bg-white/5">
            <h1 className="border-b-0 text-white text-[3rem] m-0">
              On Demand
              <br/>
              Financial APIs
            </h1>
            <p className="text-white text-base my-[0.8rem]">
              Instant access to millions of datapoints in JSON format
            </p>
            <div className="flex flex-row flex-wrap gap-4">
              <Link className="button browse text-xl py-2 px-4 my-6 me-6" to="/products/api">
                API Directory
              </Link>
              <Link className="button browse text-xl py-2 px-4 my-6 me-6" to="/data-coverage">
                Data Coverage
              </Link>
            </div>
          </div>
        </section>

        {/* Data Section */}
        <section className="flex-none m-0 border-b border-[#ddd] px-32 py-16">
          <div className="max-w-240 mx-auto">
            <h2 className="text-[3rem] mb-[0.4rem] text-center">Global Market Data</h2>
            <p className="text-[#666] text-base my-0 text-center">
              Institutional-grade financial data, accessible to every developer
            </p>
            <div className="flex-none py-10 px-2">
              <div className="flex flex-row flex-nowrap gap-8">
                <FeatureCard
                  icon={<TbWorld size={36} color="#ee6e73" stroke="#ee6e73"/>}
                  title="Real-Time &amp; Accurate"
                  body="Live quotes, historical data, and corporate actions sourced directly from exchanges and premium data providers worldwide"
                />
                <FeatureCard
                  icon={<TbBolt size={36} color="#ee6e73"/>}
                  title="Integrate in Minutes"
                  body="Clean JSON responses, comprehensive docs, and SDKs mean you go from signup to production faster — whether you're building in code, Excel, or WordPress"
                />
                <FeatureCard
                  icon={<TbCurrencyDollar size={36} color="#ee6e73"/>}
                  title="Simple, Transparent Pricing"
                  body="One contract covers everything — equities, FX, crypto, and more. Bundle datasets for volume discounts with no hidden fees"
                />
              </div>
            </div>
            <h3 className="text-[#666] text-[1.6rem] font-normal tracking-[0.08em] mt-10 mb-2 text-center uppercase">
              Trusted Data Sources
            </h3>
            <LogoStrip vendors={dataSources}/>
          </div>
        </section>

        {/* News Section */}
        <section className="flex-none m-0 border-b border-[#ddd] px-32 py-16 bg-[#f9f9fb]">
          <div className="max-w-240 mx-auto">
            <h2 className="text-[3rem] mb-[0.4rem] text-center">Financial News at Scale</h2>
            <p className="text-[#666] text-base my-0 text-center">
              Power your platform with a curated, high-velocity news feed via NewsRPM
            </p>
            <div className="flex-none py-10 px-2">
              <div className="flex flex-row flex-nowrap gap-8">
                <FeatureCard
                  white
                  icon={<TbDatabase size={36} color="#ee6e73"/>}
                  title="100+ Premium Publishers"
                  body="Breaking financial news from the world's most trusted outlets, delivered in real time so your users are always first to know"
                />
                <FeatureCard
                  white
                  icon={<TbBolt size={36} color="#ee6e73"/>}
                  title="Your Own Private Instance"
                  body="A dedicated NewsRPM deployment means zero cross-tenant bleed — you control exactly which topics, tickers, and sources reach your application"
                />
                <FeatureCard
                  white
                  icon={<TbTrendingUp size={36} color="#ee6e73"/>}
                  title="Turn News into Revenue"
                  body="Activate Paid Placements alongside your news feed to create a native advertising channel that generates income without disrupting the user experience"
                />
              </div>
            </div>
            <h3 className="text-[#666] text-[1.6rem] font-normal tracking-[0.08em] mt-10 mb-2 text-center uppercase">
              Content Providers
            </h3>
            <LogoStrip vendors={newsSources}/>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}

function FeatureCard({
                       icon,
                       title,
                       body,
                       white = false,
                     }: {
  icon: ReactNode;
  title: string;
  body: string;
  white?: boolean;
}) {
  return (
    <div
      className={`${white ? "bg-white" : "bg-white"} border border-[#eee] rounded-lg shadow-[0_2px_8px_rgb(0_0_0/0.04)] flex-[0_1_33%] p-8 px-6 text-center`}
    >
      <div className="mb-5 mx-auto w-fit">{icon}</div>
      <h3 className="text-[1.3rem] leading-[1.2] mt-0 mb-3 text-center">{title}</h3>
      <p className="text-[#555] text-[1rem] leading-[1.6] m-0">{body}</p>
    </div>
  );
}

function LogoStrip({ vendors }: { vendors: Array<{ website?: string | null | undefined, image?: string | null | undefined }> | null | undefined }) {
  return (
    <div className="flex flex-row flex-wrap gap-12 items-center justify-center border-t border-[#eee] mt-6 pt-8">
      {vendors?.map((vendor, i) =>
        vendor.image ? (
          <a key={i} className="flex-none" target="_blank" href={vendor.website!} rel="noreferrer">
            <img
              src={vendor.image}
              className="h-[1.7em] cursor-pointer grayscale opacity-45 transition-[filter,opacity] duration-300 hover:grayscale-0 hover:opacity-100"
            />
          </a>
        ) : null,
      )}
    </div>
  );
}